import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { apiMultipart } from '../utils/api';

const MarkCompleteModal = ({ milestone, open, onClose, onSuccess }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (files.length === 0) {
      toast.error('Please upload at least one photo');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      await apiMultipart.post(`/milestones/${milestone.milestone_id}/mark-complete`, formData);
      
      toast.success('Milestone marked for review. Awaiting admin approval.');
      setFiles([]);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark milestone complete');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark Milestone Complete</DialogTitle>
          <DialogDescription>
            Upload photos of completed work for {milestone?.stage_name}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="photos">Upload Photos *</Label>
            <Input
              id="photos"
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              className="cursor-pointer"
              required
            />
            {files.length > 0 && (
              <p className="text-sm text-stone-600">
                {files.length} file(s) selected
              </p>
            )}
          </div>

          <div className="bg-stone-50 border border-stone-200 rounded-sm p-3">
            <p className="text-sm text-stone-700">
              <strong>Note:</strong> After submission, this milestone will be marked as "Under Review" 
              and sent to admin for approval. Customer will be able to approve after admin review.
            </p>
          </div>

          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-orange-700 hover:bg-orange-800"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Submit
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MarkCompleteModal;
